import { css } from '@emotion/react';
import { Input, Menu, Pill, SelectV2 } from '@recruit-robin/robin-theme/web-components';
import { XClose } from '@recruit-robin/robin-theme/web-icons';
import { useMemo, useState } from 'react';

import { useDictionary, useLanguage } from '@/shared/lib/i18n';

const menuStyles = css`
  z-index: 3;
`;

const inputStyles = css`
  & > div {
    width: 100%;

    input {
      width: unset;
      min-width: 100px;
      flex: 1;
    }
  }
`;

export type SelectRequirementProps = {
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  options: { label: { en: string; nl: string }; value: string }[];
  getLabel: (value: string, language: 'en' | 'nl') => string;
};

export const SelectRequirement = ({ value, onChange, placeholder, options, getLabel }: SelectRequirementProps) => {
  const dictionary = useDictionary();
  const language = useLanguage();
  const [text, setText] = useState('');
  const safeValue = value ?? [];

  const filteredOptions = useMemo(() => {
    return options.filter((o) => o.label[language].toLowerCase().includes(text.toLowerCase()));
  }, [options, text, language]);

  const handleToggle = (v: string) => {
    onChange(safeValue.includes(v) ? safeValue.filter((o) => o !== v) : [...safeValue, v]);
  };

  const handleRemove = (v: string) => {
    onChange(safeValue.filter((o) => o !== v));
  };

  return (
    <SelectV2.Root
      inputRender={
        <Input
          customCss={inputStyles}
          readOnly
          startAdornment={safeValue.map((o) => (
            <Pill
              key={o}
              style="icon-removable"
              type="subtle-light"
              label={getLabel(o, language)}
              padding="both"
              onClick={() => handleRemove(o)}
              endIcon={<XClose size={14} />}
            />
          ))}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
        />
      }
      flipMenu={false}
      contained>
      <Menu.Root customCss={menuStyles}>
        {filteredOptions.map((o) => (
          <Menu.Item.Root key={o.value} onClick={() => handleToggle(o.value)} selected={safeValue.includes(o.value)}>
            <Menu.Item.Checkbox />
            {o.label[language]}
          </Menu.Item.Root>
        ))}
        {filteredOptions.length === 0 && <Menu.Item.Root>{dictionary.noDataAvailable}</Menu.Item.Root>}
      </Menu.Root>
    </SelectV2.Root>
  );
};
